import { siteLabel } from './sites';

// 'notion' is ACTIVE as of the Phase B activation — buildSources iterates this list to render a
// card per source. Activated together with the backend (NOTION_CLIENT_ID/SECRET/VERSION set in
// Vercel + migration 117 applied to prod + the Notion OAuth app registered), so the live "Connect
// Notion" button reaches a configured backend. Paired with the `id === 'notion'` buildSources
// spread below (type-coupled: the `as const` union narrows `id`, so that spread is a compile error
// unless 'notion' is in this list).
export const ACTIVE_SOURCES = ['chatgpt', 'claude', 'perplexity', 'claude-code', 'google', 'slack', 'slack_user', 'github', 'notion', 'linear', 'granola'] as const;
export const CONSENT_SOURCES = ['chatgpt', 'claude', 'perplexity'];
export const CONVERSATION_SOURCES = ['chatgpt', 'claude', 'perplexity'];
// The server-side-pull sources: Google, the team Slack install, the per-user Slack-DM install,
// GitHub, the team Notion install, per-user Linear, and Granola. They sync via a server-side
// credential (an OAuth token, or — for Granola — a pasted API key) rather than browser capture,
// so they share connect-gating, "synced" recency, and capture-pause-exemption behavior distinct
// from the chat sources. Plain string[] (mirrors CONVERSATION_SOURCES) so `.includes(id: string)`
// typechecks. NOTE: membership here does NOT imply pausable — Granola is server-pull + disconnectable
// but has no MVP pause endpoint (absent from the route's OAUTH_PAUSE_CONFIG), so the Manage drawer
// suppresses Pause for it specifically while still offering Disconnect.
export const OAUTH_SOURCES = ['google', 'slack', 'slack_user', 'github', 'notion', 'linear', 'granola'];
// Of the OAuth sources, slack (the workspace bot) and notion are installed ONCE per team by an
// admin — the connect flow is admin-gated and every teammate's briefing/ingestion rides the same
// shared credential (app/api/jobs/briefing-slack, app/api/jobs/notion-sync-sweep, MVP repo).
// Pausing either fully revokes that shared credential, so it stops capture/delivery for the WHOLE
// team, not just the person who clicked Pause — unlike every other pausable source (google,
// slack_user, github), which is each person's own connection. PauseConfirmDialog warns explicitly
// for these two.
export const TEAM_SHARED_OAUTH_SOURCES = ['slack', 'notion'];

export const SOURCE_META: Record<string, { name: string; trustCopy: string; signal: string }> = {
  chatgpt: {
    name: 'ChatGPT',
    trustCopy: 'Do you consent to Terum reading your ChatGPT conversation history? Terum will import your recent conversations to build your personal context layer.',
    signal: 'Prompt patterns and topic clusters',
  },
  claude: {
    name: 'Claude',
    trustCopy: 'Do you consent to Terum reading your Claude conversation history? Terum will import your recent conversations to build your personal context layer.',
    signal: 'Reasoning style and recurring asks',
  },
  perplexity: {
    name: 'Perplexity',
    trustCopy: 'We read the connected search and response history to capture your research patterns and recurring questions.',
    signal: 'Research patterns and questions asked',
  },
  'claude-code': {
    name: 'Claude Code',
    trustCopy: 'Captures your Claude Code sessions via a local hook — nothing runs in the browser.',
    signal: 'Coding decisions, debugging patterns, and architecture choices',
  },
  google: {
    name: 'Google',
    trustCopy: 'Do you consent to Terum reading your Gmail and Google Docs (read-only)? Terum mirrors the last 90 days and keeps it in sync — it never sends, edits, or deletes anything in your Google account.',
    signal: 'Email threads and document context',
  },
  slack: {
    name: 'Slack',
    trustCopy: 'Connect Slack to capture public-channel messages and deliver your daily briefing as a DM and to a #terum-briefing channel. Only a team admin can connect Slack — the bot joins public channels and stores messages; it never reads private channels or DMs.',
    signal: 'Team conversations and collaboration patterns',
  },
  github: {
    name: 'GitHub',
    trustCopy: 'Connect GitHub (read-only) to enrich your team briefing with commit diffstats and branch-divergence signals. Terum mirrors metadata for your repositories — it never writes to, edits, or deletes anything in your repos.',
    signal: 'Commit activity, diffstats, and branch divergence',
  },
  // Slack tier-3 — the per-user DM/private-channel source (provider='slack_user'),
  // distinct from the admin bot install above. Reads only YOUR own DMs into YOUR
  // private knowledge; never the team's shared graph.
  slack_user: {
    name: 'Slack DMs',
    trustCopy: 'Connect your own Slack DMs, group DMs, and private channels. Terum distills the decisions in them into your private knowledge — never the team graph, never coworkers’ own messages.',
    signal: 'Decisions made in your direct messages',
  },
  // Notion — team-scoped install (mirrors Slack's gating: one admin connects for the whole team).
  // Knowledge-graph ONLY: shared pages + database rows distill into the team's knowledge graph.
  // Notion contributes NOTHING to the people graph in v1, so the copy must never mention
  // collaboration / teamwork edges (backend notion-ingest.md §13).
  notion: {
    name: 'Notion',
    trustCopy: 'Connect Notion to distill your team’s shared pages and database knowledge into your team’s knowledge graph. Only a team admin can connect Notion — the workspace token is team-wide. Terum reads only the pages and databases you share with the Terum connection (read-only) — it never writes to, edits, or deletes anything in Notion.',
    signal: 'Shared pages and database knowledge',
  },
  // Linear — per-USER OAuth (like GitHub/Google), read-only issue-tracker mirror. Feeds the daily
  // briefing "your Linear" section + the teamwork graph. Not a team-admin install (each person
  // connects their own workspace); the copy stays read-only ("never creates/edits/closes").
  linear: {
    name: 'Linear',
    trustCopy: 'Connect Linear (read-only) to surface your assigned and overdue issues in your daily briefing and map how your team works. Terum mirrors your Linear issues, projects, and comments — it never creates, edits, closes, or comments on anything in Linear.',
    signal: 'Assigned issues, projects, and issue discussions',
  },
  // Granola — per-user, connects with a pasted API key (server-side pull, no OAuth popup / no
  // extension). Read-only mirror of meeting notes + transcripts, distilled per-topic with
  // who-participated + turn dynamics into the knowledge graph.
  granola: {
    name: 'Granola',
    trustCopy: 'Connect Granola (read-only) to distill your meetings — their topics, decisions, and who participated — into your knowledge graph. Terum mirrors your Granola meeting notes and transcripts; it never edits or deletes anything in Granola.',
    signal: 'Meetings — topics, decisions, and participants',
  },
};

export interface SiteStat {
  site: string;
  conversation_count: number;
  last_activity: string | null;
}

// Ghost Phase B (spec §3) — team coverage strip. Mirrors the backend TeamCoverageSchema
// (lib/dashboard/contracts/shared.ts). `connected` = onboarded people; `observed` = ghosts with recent
// shared-signal activity (team-wide count, window-bounded); `observedNames` = the named subset
// (Slack/GitHub/Linear-observed ghosts only, §3.3). Null on the response when the caller has no team.
export interface TeamCoverage {
  connected: number;
  observed: number;
  observedNames: string[];
  windowDays: number;
}

// bug-171: bound source-account identity + drift signal for a chat source. Mirrors
// the MVP contract (lib/dashboard/contracts/sources.ts SourceAccountSchema). All
// fields are nullable — a chat source has no source_account until the extension
// first reports an identity.
export interface SourceAccount {
  email: string | null;
  label: string | null;
  driftDetected: boolean;
  observedEmail: string | null;
  // Claude has NO account email (its identity report carries only an org label), so the observed
  // label is the only name the picker can render for a drifted Claude account. Optional rather
  // than just `| null`: a dashboard deployed ahead of the MVP field gets undefined and falls back
  // to the generic wording. MVP: buildSourceAccount, account-picker unit A (2026-07-29).
  observedLabel?: string | null;
}

// A connected Claude Code device — one active terum-capture API key. Mirrors the MVP
// contract (lib/dashboard/contracts/sources.ts CaptureDeviceSchema). `name` is the
// machine hostname captured at `terum-capture setup`. key_prefix only (never the full
// key/hash); last_used_at may be null (best-effort ingest stamp).
export interface CaptureDevice {
  id: string;
  name: string | null;
  key_prefix: string | null;
  created_at: string | null;
  last_used_at: string | null;
}

// bug-373: current sign-in state of a chat source, mirrored from the MVP contract
// (lib/dashboard/contracts/sources.ts ChatSignInStatusSchema). 'signed_out' means the
// browser has no signed-in account for this source, so it can't capture — the card must
// say so instead of showing a green "Connected". 'unknown' until the extension reports.
export type ChatSignInStatus = 'signed_in' | 'signed_out' | 'unknown';

// Uniform, server-derived "capture status" for every source card — MIRROR of the MVP contract
// (lib/dashboard/contracts/sources.ts CAPTURE_STATES/CaptureStatusSchema, invariant #6, same
// change set). Hand-synced copy: the value-set MUST match the backend literals exactly, never
// hand-typed at a comparison site (root CLAUDE.md "never compare magic enum literals" — import
// CAPTURE_STATES / CaptureState instead). The 7 states, and what the shared chip renders for each:
//   disconnected → NOT ACTIVE · paused → PAUSED · managed → neutral "managed by your team admin"
//   (a team-shared source a non-owner can't observe the health of — never a green health chip) ·
//   needs_attention → the detail (Select repositories / Not signed in / Share pages…) ·
//   syncing → SYNCING · error → the detail (Last sync failed) · live → ACTIVE (may be stale).
export const CAPTURE_STATES = [
  'disconnected',
  'paused',
  'managed',
  'needs_attention',
  'syncing',
  'error',
  'live',
] as const;
export type CaptureState = typeof CAPTURE_STATES[number];

export interface CaptureStatus {
  state: CaptureState;
  last_activity_at?: string | null; // ISO — most recent captured/synced moment
  detail?: string | null;           // short human phrase for the chip
  stale?: boolean | null;           // last activity older than this source's cadence AND not syncing
}

// The one shared chip model (spec §7): maps a capture_status → { label, color, pulse } for the card
// headline, uniformly across every source. Pure + exhaustive over CAPTURE_STATES so a new/unknown
// state can never fall through to an error screen (returns null → the card uses its legacy label).
// `managed` is deliberately neutral (var(--faint)), never a green health tone — a non-owner can't
// observe a team-shared source's health, so the chip must not assert one (spec §8). The dot is
// rendered with an inline background + an optional pulse class (there is no per-color .dot class for
// every tone, and `managed` needs a neutral one the CSS doesn't define).
export interface CaptureChip {
  label: string;
  color: string;
  pulse: boolean;
}
export function captureStatusChip(cs: CaptureStatus | null | undefined): CaptureChip | null {
  if (!cs) return null;
  switch (cs.state) {
    case 'syncing': return { label: 'SYNCING', color: 'var(--acc)', pulse: true };
    // bug-405: a stale live source (cron cadence, last sync >45min — the ONLY degraded signal the four
    // non-github cron sources have, since `error` is github-only) must NOT read as a healthy green
    // ACTIVE. Amber STALE surfaces the stalled ingestion; the recency sub-line then reads "STALE ·
    // synced 2h ago".
    case 'live': return cs.stale
      ? { label: 'STALE', color: 'var(--amb)', pulse: false }
      : { label: 'ACTIVE', color: 'var(--grn)', pulse: false };
    case 'paused': return { label: 'PAUSED', color: 'var(--amb)', pulse: false };
    case 'needs_attention': return { label: (cs.detail?.trim() || 'Needs attention').toUpperCase(), color: 'var(--amb)', pulse: false };
    case 'error': return { label: (cs.detail?.trim() || 'Error').toUpperCase(), color: 'var(--rose)', pulse: false };
    case 'managed': return { label: 'CONNECTED', color: 'var(--faint)', pulse: false };
    case 'disconnected': return { label: 'NOT ACTIVE', color: 'var(--rose)', pulse: false };
    default: return null; // unhandled state → caller falls back to the legacy label, never crashes
  }
}

// bug-414: sort rank for the unified Sources grid (top → bottom): problems first, then healthy,
// then off. Buckets by capture_status.state (+ .stale) — the SAME field captureStatusChip above
// renders from — so a card's POSITION and its CHIP always tell the same story. (Before this, the
// grid sorted on the coarse legacy source.status while the chip read capture_status, so a
// STALE/needs_attention source scattered into the healthy cluster instead of floating up.) Keep
// this in lockstep with captureStatusChip: never migrate one to capture_status without the other.
// Ryan-approved tier order:
//   0 error · 1 needs_attention · 2 live+stale · 3 syncing · 4 live+healthy · 5 managed · 6 paused · 7 disconnected
// `capturing` is the real-time extension-backfill signal for chat sources (SourcesSurface's
// deriveCapturing) — a DIFFERENT signal from a backend state:'syncing' (it fires for a chat source
// mid-backfill the backend still reports as `live`), so it's kept, folded into the SYNCING tier.
// Falls back to the legacy source.status buckets ONLY when capture_status is absent (e.g.
// claude-code, which is device-driven and has no integration row).
export function sourceSortRank(source: Source, capturing: boolean): number {
  const cs = source.capture_status;
  if (cs) {
    switch (cs.state) {
      case 'error': return 0;
      case 'needs_attention': return 1;
      case 'live': return cs.stale ? 2 : (capturing ? 3 : 4);
      case 'syncing': return 3;
      case 'managed': return 5;
      case 'paused': return 6;
      case 'disconnected': return 7;
    }
  }
  // No capture_status → preserve the legacy source.status precedence
  // (needs-attention/signed-out > capturing > active > paused > rest), on the same tier scale.
  if (source.status === 'needs-attention' || source.status === 'signed-out') return 1;
  if (capturing) return 3;
  if (source.status === 'active') return 4;
  if (source.status === 'paused') return 6;
  return 7;
}

export interface Integration {
  provider: string;
  status: string;
  last_synced_at: string | null;
  backfill_complete?: boolean;
  item_counts?: { emails: number; docs: number; calendar: number };
  // Calendar scope-present flag (provider='google' only) — true once the calendar sync has
  // written its cursor; false for a Google connection made before the calendar.readonly scope.
  // Mirrors the MVP contract LiveIntegrationSchema.calendar_sync_enabled (invariant #6).
  calendar_sync_enabled?: boolean;
  channel_count?: number;
  team_channel_missing?: boolean;
  // The currently-selected team-briefing channel id (null until chosen) + the joined-
  // channel list — together they drive the "post team briefing to" picker, so a
  // workspace that blocked auto-creating #terum-briefing can point briefings at an
  // existing channel instead. Slack-only.
  team_channel_id?: string | null;
  channels?: { id: string; name: string | null }[];
  // bug-196: live slack_messages count — the Slack card's headline metric (Slack is
  // store-only, so it has no compacted conversation_count). Present for slack only.
  message_count?: number;
  // bug-314: GitHub read-only-mirror metrics — repos connected + OPEN PRs updated in the
  // last 14 days. Present for github only (GitHub is never compacted into the user's own
  // conversation_count, and its distill rows are team-owned → user_id-blind).
  repo_count?: number;
  pr_count?: number;
  // GitHub repo-selection state (github only): how many repos the user chose to ingest, and whether
  // the required post-connect "pick repositories" step is still unfinished (connected but none
  // chosen → the card prompts to choose instead of showing 0/0 mirror metrics).
  selected_repo_count?: number;
  repos_pending?: boolean;
  // Notion store-only metrics (provider='notion' only). page_count = mirrored notion_pages count
  // (Notion is never compacted → its compacted conversation_count is always 0). shared_pages_hint
  // is true when page_count===0 (connected but nothing shared with the integration yet). Mirrors
  // the MVP contract LiveIntegrationSchema page_count/shared_pages_hint (invariant #6).
  page_count?: number;
  shared_pages_hint?: boolean;
  // Linear read-only-mirror metrics (provider='linear' only): issues + projects mirrored. Like
  // GitHub/Notion, Linear is never compacted into conversation_count. Mirrors the MVP contract
  // LiveIntegrationSchema issue_count/project_count.
  issue_count?: number;
  project_count?: number;
  // Granola store-only metric (provider='granola' only): live granola_meetings count. Granola's
  // topics DO compact, but a meeting fans out into several topic rows, so the card headline is the
  // meeting count, not conversation_count. Mirrors the MVP sources route's granola meeting_count.
  meeting_count?: number;
  // Linear team-selection state, surfaced on the Linear card: how many teams are chosen for ingest,
  // and teams_pending = connected-but-none-chosen (drives the "choose teams" setup prompt).
  selected_team_count?: number;
  teams_pending?: boolean;
  // bug-197/198: durable CAPTURING signal for chat providers — derived server-side
  // from a backfill_started_at stamp, so it survives navigation/reload/fresh extension.
  backfill_in_progress?: boolean;
  // bug-280: still-ingesting signal — true while capture_events remain uncompacted
  // (compaction lags capture). Folded into CAPTURING so the card stays CAPTURING through
  // the compaction stage + live capture, not just the brief raw-backfill window. Chat-only.
  processing?: boolean;
  // bug-109 / latent #2: a run that finished with some conversations still needing a detail
  // re-fetch ends PARTIAL. The backend already sends these; the SPA must render an honest
  // "imported, N still need detail" instead of a clean green "complete".
  backfill_partial?: boolean;
  incomplete_count?: number;
  // bug-171: present for chat providers (chatgpt/claude/perplexity); null until bound.
  source_account?: SourceAccount | null;
  // bug-373: current sign-in state + last check time for chat providers. Drives the
  // "not signed in" card state so a connected-but-signed-out source isn't shown as healthy.
  sign_in_status?: ChatSignInStatus | null;
  last_identity_check_at?: string | null;
  paused?: boolean;
  paused_at?: string | null;
  // bug-395: for a team-shared source (slack/notion), whether the viewer's OWN team has it
  // connected — set by the backend even when the viewer doesn't personally own the integration
  // row (the admin does). Lets a non-admin see a read-only "managed by your team admin" card
  // instead of a dead, always-403 Activate. Mirrors the MVP contract LiveIntegrationSchema
  // team_connected (invariant #6). Present only for slack/notion; absent elsewhere.
  team_connected?: boolean;
  // Uniform live/health status (see CaptureStatus above), mirrored from the MVP contract
  // LiveIntegrationSchema.capture_status. Present on every integration entry the backend returns;
  // absent for a source with no integration row (e.g. claude-code, which is device-driven).
  capture_status?: CaptureStatus | null;
}

export interface Source {
  id: string;
  name: string;
  status: string;
  conversation_count: number;
  last_activity: string | null;
  last_synced_at: string | null;
  backfill_complete: boolean;
  item_counts?: { emails: number; docs: number; calendar: number };
  // Calendar scope-present flag, surfaced on the Google card (drives "Reconnect to enable
  // Calendar" when false + the meetings count when true).
  calendar_sync_enabled?: boolean;
  channel_count?: number;
  team_channel_missing?: boolean;
  // Selected team-briefing channel + joined-channel list, surfaced on the Slack card
  // for the "post team briefing to" picker.
  team_channel_id?: string | null;
  channels?: { id: string; name: string | null }[];
  // bug-196: live slack_messages count, surfaced as the Slack card metric.
  message_count?: number;
  // bug-314: GitHub read-only-mirror metrics (repos connected + OPEN PRs, last 14 days),
  // surfaced as the GitHub card metric in place of the always-0 conversation_count.
  repo_count?: number;
  pr_count?: number;
  // GitHub repo-selection state, surfaced on the GitHub card: how many repos are chosen for ingest,
  // and repos_pending = connected-but-none-chosen (drives the "choose repositories" setup prompt).
  selected_repo_count?: number;
  repos_pending?: boolean;
  // Notion store-only metrics, surfaced as the Notion card metric (pages distilled) in place of
  // the always-0 conversation_count. shared_pages_hint flags "connected but nothing shared yet".
  page_count?: number;
  shared_pages_hint?: boolean;
  // Linear read-only-mirror metrics (issues + projects), surfaced as the Linear card metric.
  issue_count?: number;
  project_count?: number;
  // Granola meeting count, surfaced as the Granola card metric (in place of conversation_count).
  meeting_count?: number;
  // Linear team-selection state, surfaced on the Linear card: how many teams are chosen for ingest,
  // and teams_pending = connected-but-none-chosen (drives the "choose teams" setup prompt).
  selected_team_count?: number;
  teams_pending?: boolean;
  // bug-197/198: durable CAPTURING signal, surfaced on the chat source card.
  backfill_in_progress?: boolean;
  // bug-280: still-ingesting (uncompacted capture_events) — surfaced on the card, folded
  // into CAPTURING so it covers the compaction stage + live capture, not just raw backfill.
  processing?: boolean;
  // bug-109 / latent #2: surfaced on the chat source card as an honest partial state.
  backfill_partial?: boolean;
  incomplete_count?: number;
  // bug-171: bound source identity + drift, surfaced on the source card.
  source_account?: SourceAccount | null;
  // bug-373: current sign-in state + last check time, surfaced on the chat source card
  // (the not-signed-in state + a "checked Xm ago" line).
  sign_in_status?: ChatSignInStatus | null;
  last_identity_check_at?: string | null;
  paused?: boolean;
  paused_at?: string | null;
  // bug-395: team-shared source (slack/notion) is connected at the TEAM level, surfaced on the
  // card so a non-admin sees a read-only "managed by your team admin" status instead of a dead
  // Activate. Threaded through buildSources for TEAM_SHARED_OAUTH_SOURCES only.
  team_connected?: boolean;
  // Uniform live/health status, threaded from the integration through buildSources — the one
  // shared chip in SourcesSurface reads this. Null for a source with no integration row.
  capture_status?: CaptureStatus | null;
  // Connected Claude Code devices (active terum-capture API keys), surfaced on the
  // claude-code card's Devices drawer. Present for claude-code only.
  devices?: CaptureDevice[];
}

// bug-395: is `userId` an admin of this team (used to gate the team-shared source controls —
// only admins get Activate/Manage on slack/notion; everyone else gets a read-only status card).
// Pure + null-safe so it's unit-testable and safe before team settings/current user load.
export function isTeamAdmin(
  members: { user_id: string; role: string }[] | undefined | null,
  userId: string | null | undefined,
): boolean {
  if (!members || !userId) return false;
  return members.find((m) => m.user_id === userId)?.role === 'admin';
}

export function sourceName(id: string): string {
  return SOURCE_META[id]?.name || siteLabel(id) || id;
}

export function buildSources(sites: SiteStat[], integrations: Integration[], opts?: { hasApiKey?: boolean; capturePaused?: boolean; devices?: CaptureDevice[] }): Source[] {
  const siteMap = new Map(sites.map(s => [s.site, s]));
  const integrationMap = new Map(integrations.map(i => [i.provider, i]));

  return ACTIVE_SOURCES.map(id => {
    const site = siteMap.get(id);
    const integration = integrationMap.get(id);

    let status: string;
    if (id === 'claude-code') {
      // Device-driven (decision C): ACTIVE iff at least one live device (api_key) exists.
      // No device but historical data → NOT ACTIVE, yet the card still offers delete-data.
      status = (opts?.devices?.length ?? 0) > 0 ? 'active' : 'not-active';
    } else if (OAUTH_SOURCES.includes(id)) {
      // A paused OAuth source has integration.status='disconnected' in the DB — pausing one
      // of these REVOKES its token (there's no per-request gate to flip for a server-side
      // pull, so the only real way to stop one is taking away its ability to authenticate),
      // and integration.paused=true is the marker that distinguishes that from a genuine
      // disconnect. Resuming is a fresh OAuth connect, not a flag flip — there's no token
      // left to re-arm.
      status = integration?.status === 'connected' ? 'active' : (integration?.paused ? 'paused' : 'not-active');
    } else {
      status = (integration?.status === 'connected') ? 'active' : (site && !integration ? 'active' : 'not-active');
    }

    // Global capture kill switch downgrades active browser-capture sources to "paused".
    // It does NOT apply to the OAuth sources (Google, the Slack workspace bot, the per-user
    // Slack-DM tier-3 source, or GitHub) — none of those capture via the browser.
    if (opts?.capturePaused && status === 'active' && !OAUTH_SOURCES.includes(id)) {
      status = 'paused';
    }
    // Per-source user pause for chat sources: connected but recording switched off via a flag
    // flip (the token-revoke OAuth case above already derives 'paused' from DB status, so it
    // doesn't need this branch).
    if (integration?.paused && status === 'active' && CONVERSATION_SOURCES.includes(id)) {
      status = 'paused';
    }
    // bug-171: drift is NOT a user pause — a different account is logged in. Its own
    // surfaced state, never collapsed into 'paused'. Drift overrides both 'active' AND
    // 'paused' so a source that is paused AND drifted surfaces as 'needs-attention'.
    // It does NOT override 'not-active' (a disconnected source stays disconnected).
    if (integration?.source_account?.driftDetected && (status === 'active' || status === 'paused') && CONVERSATION_SOURCES.includes(id)) {
      status = 'needs-attention';
    }
    // bug-373: a connected chat source whose browser session is signed OUT can capture
    // nothing — surface its own 'signed-out' state so a green "ACTIVE" never lies. Only
    // overrides 'active'; an explicit user pause or a drift is the dominant reason and wins.
    if (integration?.sign_in_status === 'signed_out' && status === 'active' && CONVERSATION_SOURCES.includes(id)) {
      status = 'signed-out';
    }

    return {
      id,
      name: sourceName(id),
      status,
      conversation_count: site?.conversation_count ?? 0,
      last_activity: site?.last_activity ?? null,
      last_synced_at: integration?.last_synced_at ?? null,
      backfill_complete: integration?.backfill_complete ?? false,
      // Surfaced for every provider — see the status-derivation comment above for how chat vs.
      // OAuth sources each reach 'paused' differently.
      paused: integration?.paused ?? false,
      paused_at: integration?.paused_at ?? null,
      // Uniform live/health status — the one shared chip reads this (spec §7). Present on every
      // integration entry the backend returns; null for a source with no integration row (e.g.
      // claude-code, which is device-driven → the chip falls back to the legacy status label).
      capture_status: integration?.capture_status ?? null,
      // bug-395: whether the viewer's team has this team-shared source connected (slack/notion
      // only). Drives the non-admin read-only "managed by your team admin" card in SourcesSurface.
      ...(TEAM_SHARED_OAUTH_SOURCES.includes(id)
        ? { team_connected: integration?.team_connected ?? false }
        : {}),
      ...(id === 'google'
        ? {
            item_counts: integration?.item_counts ?? { emails: 0, docs: 0, calendar: 0 },
            calendar_sync_enabled: integration?.calendar_sync_enabled ?? false,
          }
        : {}),
      ...(id === 'slack'
        ? {
            channel_count: integration?.channel_count ?? 0,
            team_channel_missing: integration?.team_channel_missing ?? false,
            team_channel_id: integration?.team_channel_id ?? null,
            channels: integration?.channels ?? [],
            message_count: integration?.message_count ?? 0,
          }
        : {}),
      // bug-314: GitHub read-only-mirror metrics — repos connected + OPEN PRs updated in
      // the last 14 days. Replaces the always-0 conversation_count on the GitHub card.
      ...(id === 'github'
        ? {
            repo_count: integration?.repo_count ?? 0,
            pr_count: integration?.pr_count ?? 0,
            selected_repo_count: integration?.selected_repo_count ?? 0,
            repos_pending: integration?.repos_pending ?? false,
          }
        : {}),
      // Notion store-only metrics — mirrored pages, in place of the always-0 conversation_count.
      // shared_pages_hint = connected but nothing shared with the Terum connection yet. Type-coupled
      // to 'notion' in ACTIVE_SOURCES above (the `as const` union narrows `id`).
      ...(id === 'notion'
        ? {
            page_count: integration?.page_count ?? 0,
            shared_pages_hint: integration?.shared_pages_hint ?? false,
          }
        : {}),
      // Linear read-only-mirror metrics — issues + projects, in place of the always-0
      // conversation_count. Type-coupled to 'linear' in ACTIVE_SOURCES (the `as const` union).
      ...(id === 'linear'
        ? {
            issue_count: integration?.issue_count ?? 0,
            project_count: integration?.project_count ?? 0,
            selected_team_count: integration?.selected_team_count ?? 0,
            teams_pending: integration?.teams_pending ?? false,
          }
        : {}),
      // Granola meeting count — the card headline metric (a meeting fans out into several
      // compacted topic rows, so conversation_count would over-count). Type-coupled to 'granola'
      // in ACTIVE_SOURCES (the `as const` union).
      ...(id === 'granola'
        ? {
            meeting_count: integration?.meeting_count ?? 0,
          }
        : {}),
      // Claude Code connected devices (active terum-capture keys) → the Devices drawer.
      ...(id === 'claude-code' ? { devices: opts?.devices ?? [] } : {}),
      // Slack tier-3 (per-user DMs): thread the honest backfill/partial signal so the
      // card can surface "importing" vs "N DMs still need a re-fetch" (no-silent-degradation).
      // backfill_complete is mapped top-level above; the partial fields ride here.
      ...(id === 'slack_user'
        ? {
            backfill_partial: integration?.backfill_partial ?? false,
            incomplete_count: integration?.incomplete_count ?? 0,
          }
        : {}),
      ...(CONVERSATION_SOURCES.includes(id)
        ? {
            source_account: integration?.source_account ?? null,
            // bug-373: thread sign-in state + last check time so the card can render the
            // not-signed-in state (and a "checked Xm ago" line).
            sign_in_status: integration?.sign_in_status ?? null,
            last_identity_check_at: integration?.last_identity_check_at ?? null,
            backfill_in_progress: integration?.backfill_in_progress ?? false,
            // bug-280: thread the still-ingesting signal so CAPTURING covers compaction.
            processing: integration?.processing ?? false,
            // bug-109 / latent #2: thread the partial signal so the card tells the truth.
            backfill_partial: integration?.backfill_partial ?? false,
            incomplete_count: integration?.incomplete_count ?? 0,
          }
        : {}),
    };
  });
}

// Ingest is in-flight for some chat source iff the server's durable backfill in-progress
// flag is set OR capture_events remain uncompacted (bug-280 `processing`). Drives the
// sources hook's poll-while-running (latent #4) so the card syncs with the long-running
// job — the raw backfill AND the slower compaction stage — instead of waiting on a single
// extension postMessage. Polling through `processing` is what lets the CAPTURING badge
// flip back to ACTIVE on its own once compaction drains.
export function hasActiveBackfill(integrations: Integration[]): boolean {
  return integrations.some((i) => i.backfill_in_progress === true || i.processing === true);
}
